import React, { useState, useEffect } from 'react';
import LocalFileSystemService, { LocalFile, LocalFolder, FileSystemNode } from '../services/fileSystemService';

interface FileSystemExplorerProps {
  onFileSelect?: (file: LocalFile) => void;
  onFileDoubleClick?: (file: LocalFile) => void;
  onFileDragStart?: (file: LocalFile) => void;
  className?: string;
  showSearch?: boolean;
  allowDragDrop?: boolean;
  customFolder?: {id: string, name: string, path: string, dirHandle?: any, files?: File[]} | null;
}

interface DragState {
  isDragging: boolean;
  draggedFile?: LocalFile;
}

const FileSystemExplorer: React.FC<FileSystemExplorerProps> = ({
  onFileSelect,
  onFileDoubleClick,
  onFileDragStart,
  className = '',
  showSearch = true,
  allowDragDrop = true,
  customFolder = null
}) => {
  // State management
  // const [folders, setFolders] = useState<LocalFolder[]>([]);  // Removed - only using native folder selection
  // const { openFile } = useOpenInCanvas(); // Currently handled by parent component
  const [currentFolder, setCurrentFolder] = useState<LocalFolder | null>(null);
  const [fileTree, setFileTree] = useState<FileSystemNode[]>([]);
  const [selectedFile, setSelectedFile] = useState<LocalFile | null>(null);
  // Search functionality removed for simplicity
  // const [searchQuery, setSearchQuery] = useState('');
  // const [searchResults, setSearchResults] = useState<LocalFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragState, setDragState] = useState<DragState>({ isDragging: false });
  
  // Navigation state
  // Navigation state - removed as we're not using navigation history for native folders
  // const [currentPath, setCurrentPath] = useState('/');
  // const [pathHistory, setPathHistory] = useState<string[]>(['/']);
  // const [historyIndex, setHistoryIndex] = useState(0);

  // Refs - search functionality removed
  // const searchInputRef = useRef<HTMLInputElement>(null);

  // Load files when customFolder changes - prevent loops with stable reference
  useEffect(() => {
    if (!customFolder) {
      setLoading(false);
      setFileTree([]);
      return;
    }

    // Use a stable identifier to prevent re-runs on same folder
    const folderId = customFolder.id;
    let isCancelled = false;

    const loadFiles = async () => {
      if (isCancelled) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('📁 Loading files from custom folder:', folderId);
        
        // Create a LocalFolder object from the custom folder
        const customLocalFolder: LocalFolder = {
          id: customFolder.id,
          name: customFolder.name,
          fullPath: customFolder.path,
          type: 'custom',
          description: 'Custom selected folder'
        };

        if (isCancelled) return;

        // Set current folder
        setCurrentFolder(customLocalFolder);

        // Load files using browser APIs or backend fallback
        const files = await LocalFileSystemService.getCustomFolderFiles(customFolder);
        
        if (isCancelled) return;
        
        const tree = LocalFileSystemService.buildFileTree(files);
        setFileTree(tree);

      } catch (err) {
        if (isCancelled) return;
        console.error('❌ Error loading custom folder:', err);
        setError(`Failed to load folder: ${customFolder.path}`);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadFiles();

    // Cleanup function to cancel async operation
    return () => {
      isCancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFolder?.id]); // Only depend on stable ID to prevent infinite loops

  // Removed duplicate - using React.useCallback version above

  // selectFolder removed - only using native folder selection now
  // const selectFolder = async (folder: LocalFolder) => { ... };

  const navigateToPath = async (path: string, folderId?: string) => {
    if (!currentFolder && !folderId) return;
    
    setLoading(true);
    const targetFolderId = folderId || currentFolder!.id;
    
    try {
      const files = path === '/' 
        ? await LocalFileSystemService.getRootFiles(targetFolderId)
        : await LocalFileSystemService.getFiles(targetFolderId, path);
      
      const tree = LocalFileSystemService.buildFileTree(files);
      setFileTree(tree);
      
    } catch (err) {
      console.error('❌ Error navigating to path:', err);
      setError(`Failed to load files from path: ${path}`);
    } finally {
      setLoading(false);
    }
  };

  const expandFolder = async (node: FileSystemNode) => {
    if (node.item.type !== 'folder' || !customFolder) return;
    
    node.isLoading = true;
    setFileTree([...fileTree]); // Force re-render
    
    try {
      const files = await LocalFileSystemService.getFiles(
        customFolder.id, 
        node.item.relativePath,
        customFolder // Pass the custom folder with dirHandle for native API support
      );
      const childTree = LocalFileSystemService.buildFileTree(files);
      
      node.children = childTree;
      node.isExpanded = true;
      node.isLoading = false;
      
      setFileTree([...fileTree]);
    } catch (err) {
      console.error('❌ Error expanding folder:', err);
      node.isLoading = false;
      setFileTree([...fileTree]);
    }
  };

  const collapseFolder = (node: FileSystemNode) => {
    node.isExpanded = false;
    setFileTree([...fileTree]);
  };

  // Search functions removed - not needed for simplified native folder browsing
  // const handleSearch = async () => { ... };
  // const clearSearch = () => { ... };

  const handleFileClick = (file: LocalFile, node?: FileSystemNode) => {
    setSelectedFile(file);
    onFileSelect?.(file);
    
    if (file.type === 'folder' && node) {
      // Since we removed the arrow buttons, folders expand/collapse on click
      if (node.isExpanded) {
        collapseFolder(node);
      } else {
        expandFolder(node);
      }
    }
  };

  const handleFileDoubleClick = (file: LocalFile) => {
    onFileDoubleClick?.(file);
    
    if (file.type === 'folder') {
      navigateToPath(file.relativePath);
    }
    // Files will be handled by the parent component to show in canvas
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, file: LocalFile) => {
    if (!allowDragDrop) return;
    
    setDragState({ isDragging: true, draggedFile: file });
    
    // Set drag data for external drops
    e.dataTransfer.setData('text/plain', `📁 ${file.name}`);
    e.dataTransfer.setData('application/json', JSON.stringify(file));
    e.dataTransfer.effectAllowed = 'copy';
    
    onFileDragStart?.(file);
  };

  const handleDragEnd = () => {
    setDragState({ isDragging: false });
  };

  // Navigation functions removed - not needed for simplified native folder browsing
  // const navigateBack = () => { ... };
  // const navigateForward = () => { ... };

  // Render file tree node
  const renderFileNode = (node: FileSystemNode, depth: number = 0): React.ReactNode => {
    const { item } = node;
    const isSelected = selectedFile?.id === item.id;
    
    return (
      <div key={item.id} style={{ marginLeft: `${depth * 20}px` }}>
        <div
          className={`file-item ${isSelected ? 'selected' : ''} ${dragState.isDragging && dragState.draggedFile?.id === item.id ? 'dragging' : ''}`}
          onClick={() => handleFileClick(item, node)}
          onDoubleClick={() => handleFileDoubleClick(item)}
          draggable={allowDragDrop}
          onDragStart={(e) => handleDragStart(e, item)}
          onDragEnd={handleDragEnd}
          title={item.name} // Tooltip showing full file name
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 8px',
            cursor: 'pointer',
            borderRadius: '4px',
            backgroundColor: isSelected ? 'var(--obsidian-accent-bg)' : 'transparent',
            color: isSelected ? 'var(--obsidian-accent-text)' : 'var(--obsidian-text-primary)',
            border: isSelected ? '1px solid var(--obsidian-accent)' : '1px solid transparent',
            userSelect: 'none',
            transition: 'all 0.2s ease',
            opacity: dragState.isDragging && dragState.draggedFile?.id === item.id ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              (e.target as HTMLElement).style.backgroundColor = 'var(--obsidian-hover-bg)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              (e.target as HTMLElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          {/* Folder expand/collapse arrow */}
          {item.type === 'folder' && (
            <button
              className="folder-toggle"
              onClick={(e) => {
                e.stopPropagation();
                if (node.isExpanded) {
                  collapseFolder(node);
                } else {
                  expandFolder(node);
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--obsidian-text-secondary)',
                cursor: 'pointer',
                padding: '0',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {node.isLoading ? (
                <div className="loading-dot" style={{ transform: 'scale(0.5)' }}></div>
              ) : (
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  {node.isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                </span>
              )}
            </button>
          )}
          
          {/* File/folder icon */}
          <span 
            className="material-symbols-outlined file-icon"
            style={{ 
              fontSize: '18px',
              color: item.type === 'folder' ? 'var(--obsidian-folder-color, #8b949e)' : 'var(--obsidian-file-color, #7c3aed)'
            }}
          >
            {item.icon || (item.type === 'folder' ? 'folder' : 'description')}
          </span>
          
          {/* File name */}
          <div className="file-info" style={{ flex: 1, minWidth: 0 }}>
            <div 
              className="file-name"
              style={{
                fontSize: '13px',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {item.name}
            </div>
            
            {/* File metadata */}
            <div 
              className="file-meta"
              style={{
                fontSize: '11px',
                color: 'var(--obsidian-text-secondary)',
                marginTop: '1px'
              }}
            >
              {item.type === 'file' && item.size && (
                <span>{LocalFileSystemService.formatFileSize(item.size)} • </span>
              )}
              <span>{LocalFileSystemService.formatDate(item.lastModified)}</span>
            </div>
          </div>
        </div>
        
        {/* Render children if expanded */}
        {node.isExpanded && node.children && (
          <div className="folder-children">
            {node.children.map(child => renderFileNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`file-system-explorer ${className}`}>
      {/* Simple folder header - clean, minimal */}
      {customFolder && (
        <div style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--obsidian-border)',
          fontSize: '12px',
          color: 'var(--obsidian-text-secondary)',
          backgroundColor: 'var(--obsidian-bg-secondary)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>folder</span>
          <span title={customFolder.path} style={{ 
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' 
          }}>
            {customFolder.name}
          </span>
        </div>
      )}



      {/* Content area */}
      <div className="explorer-content" style={{
        flex: 1,
        overflowX: 'hidden', // No horizontal scrollbar
        overflowY: 'auto',   // Only vertical scrolling
        padding: '8px'
      }}>
        {loading && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            color: 'var(--obsidian-text-secondary)'
          }}>
            <div className="loading-dots">
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
            </div>
            <span style={{ marginLeft: '12px' }}>Loading files...</span>
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: 'var(--obsidian-error-bg)',
            color: 'var(--obsidian-error-text)',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            ❌ {error}
            <button
              onClick={() => window.location.reload()}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: '1px solid currentColor',
                color: 'inherit',
                borderRadius: '3px',
                padding: '2px 8px',
                cursor: 'pointer',
                fontSize: '11px'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* File tree */}
            {fileTree.length > 0 && (
              <div className="file-tree">
                {fileTree.map(node => renderFileNode(node))}
              </div>
            )}

            {/* Empty state */}
            {fileTree.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'var(--obsidian-text-secondary)',
                fontSize: '14px'
              }}>
                <span className="material-symbols-outlined" style={{ 
                  fontSize: '48px', 
                  display: 'block', 
                  marginBottom: '12px',
                  opacity: 0.5
                }}>
                  folder_open
                </span>
                {currentFolder ? 'No files found in this location' : 'Select a folder to browse files'}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Drag and drop indicator */}
      {dragState.isDragging && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'var(--obsidian-accent)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          zIndex: 1000,
          pointerEvents: 'none'
        }}>
          📁 Dragging: {dragState.draggedFile?.name}
        </div>
      )}
    </div>
  );
};

export default FileSystemExplorer;
